'use client';

import { memo, useMemo } from 'react';
import { ChevronDown, TriangleAlert } from 'lucide-react';
import { Button } from '@repo/ui/components/button';
import {
  ChartPanel,
  ChartPanelLegendItem
} from '@repo/ui/components/chart-panel';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger
} from '@repo/ui/components/dropdown-menu';
import { Skeleton } from '@repo/ui/components/skeleton';
import type {
  GradientChartDataPoint,
  GradientChartMarker,
  GradientChartSeries
} from '@repo/ui/components/gradient-chart';
import { GradientChart } from '@/components/lazy-charts';
import {
  benchmarkColor,
  formatGrowthTick,
  GROWTH_SUBJECT_COLOR,
  growthYDomain
} from '@/lib/growth-chart';
import { track } from '@/lib/analytics';
import { formatFixed } from '@/lib/format/number/fixed';
import { formatPercent } from '@/lib/format/percent/percent';

import type { InvestorPerformance } from '@/lib/investor-analytics';
import type { InvestorTransaction } from '../types/api';
import {
  BENCHMARK_OPTIONS,
  benchmarkLabel,
  type BenchmarkSymbol
} from '@/lib/benchmarks';
import { describePerformanceWarnings } from '../utils/performance-warnings';
import {
  benchmarkKey,
  buildFlowMarkers,
  buildGrowthSeries,
  GROWTH_BASE
} from '../utils/portfolio-performance';
import { PeriodReturns, type PeriodReturnRow } from './period-returns';
import { MethodologySheet } from './portfolio-performance-methodology';

interface PortfolioPerformanceProps {
  data?: InvestorPerformance;
  /** Deposits and withdrawals, used only to annotate the chart. The return
   *  line no longer depends on them — the backend serves the index. */
  transactions?: InvestorTransaction[];
  isLoading?: boolean;
  isError?: boolean;
  benchmarks: BenchmarkSymbol[];
  onBenchmarksChange: (symbols: BenchmarkSymbol[]) => void;
  /** The period table's rows, queried by the view. */
  periodRows: PeriodReturnRow[];
  /** Daily benchmark closes for the embedded period table. */
  periodBenchmark?: InvestorPerformance['benchmarks'][number];
  periodBenchmarkLoading?: boolean;
  periodBenchmarkError?: boolean;
}

/** Axis ticks read in whole dollars. */
const formatGrowthAxis = (value: number) => `$${Math.round(value)}`;

/** The level, and how far it sits from the 100 every line starts at. */
const formatGrowthValue = (value: number) =>
  `$${formatFixed(value, { digits: 2 })} · ${formatPercent(value - GROWTH_BASE, { digits: 1, sign: 'plus-minus' })}`;

interface GrowthChartProps {
  data: GradientChartDataPoint[];
  series: GradientChartSeries[];
  markers: GradientChartMarker[];
  yDomain: [number, number] | undefined;
}

/**
 * The chart behind a memo boundary. The panel around it re-renders on every
 * query update on the page (thirteen queries poll here); with stable props
 * the chart itself only redraws when its own series changes.
 */
const GrowthChart = memo(function GrowthChart({
  data,
  series,
  markers,
  yDomain
}: GrowthChartProps) {
  return (
    <GradientChart
      noCard
      hideLegend
      title=""
      data={data}
      series={series}
      markers={markers}
      height={288}
      xTickFormatter={formatGrowthTick}
      yTickFormatter={formatGrowthAxis}
      yDomain={yDomain}
      valueFormatter={formatGrowthValue}
    />
  );
});

/**
 * Net return vs benchmark (PO2, node 1787:926): growth-of-$100 portfolio
 * index against a real index overlay at full card width, deposit/withdrawal
 * annotations from the transaction ledger, and the period-returns table
 * beside it in the same card — the chart draws the shape, the table states
 * the numbers.
 * Volatility, drawdown, and the risk ratios live on the Risk screen.
 */
export function PortfolioPerformance({
  data,
  transactions,
  isLoading = false,
  isError = false,
  benchmarks,
  onBenchmarksChange,
  periodRows,
  periodBenchmark,
  periodBenchmarkLoading = false,
  periodBenchmarkError = false
}: PortfolioPerformanceProps) {
  const growth = useMemo(() => (data ? buildGrowthSeries(data) : []), [data]);
  const markers = useMemo(
    () => buildFlowMarkers(growth, transactions),
    [growth, transactions]
  );
  const chartData = useMemo<GradientChartDataPoint[]>(
    () => growth.map((point) => ({ ...point })),
    [growth]
  );
  // Frame the data instead of zero-basing: an indexed series that moved
  // little (or not at all) must sit mid-chart with its moves readable —
  // never pinned flat against the top edge of a 0–100 axis.
  const yDomain = useMemo(() => growthYDomain(growth), [growth]);

  // Only overlay indexes the backend actually returned for this window.
  const servedBenchmarks = data?.benchmarks;
  const series = useMemo(
    () => [
      { key: 'portfolio', name: 'Your return', color: GROWTH_SUBJECT_COLOR },
      ...(servedBenchmarks ?? []).map((benchmark, position) => ({
        key: benchmarkKey(benchmark.symbol),
        name: benchmarkLabel(benchmark),
        color: benchmarkColor(position)
      }))
    ],
    [servedBenchmarks]
  );

  // A wallet with money in it and no line to draw is not a wallet that has
  // not deposited yet. The index is absent (an older API) or every level in
  // this window is null, and the reader is told that instead.
  const isFunded =
    data != null &&
    (data.firstDepositAt != null ||
      data.portfolio.some((point) => point.value > 0));
  const warnings = useMemo(
    () => (data ? describePerformanceWarnings(data.warnings) : []),
    [data]
  );

  const toggleBenchmark = (symbol: BenchmarkSymbol, enabled: boolean) => {
    onBenchmarksChange(
      enabled
        ? [...benchmarks, symbol]
        : benchmarks.filter((entry) => entry !== symbol)
    );
    track('portfolio.benchmark_changed', { benchmark: symbol, enabled });
  };

  const selectedNames = BENCHMARK_OPTIONS.filter((option) =>
    benchmarks.includes(option.symbol)
  ).map((option) => option.name);
  const triggerLabel =
    selectedNames.length === 0
      ? 'Benchmarks'
      : selectedNames.length === 1
        ? selectedNames[0]!
        : `${selectedNames.length} benchmarks`;

  return (
    <ChartPanel
      title="Performance vs benchmarks"
      headingLevel="h2"
      description="Growth of $100 · your deposits and withdrawals are removed, so this compares like for like"
      periodSelector={
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              aria-label="Benchmark indexes"
              className="min-w-36 justify-between font-normal"
            >
              {triggerLabel}
              <ChevronDown className="size-4 opacity-60" aria-hidden />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="border-rowa-border-glass-highlight bg-rowa-surface-overlay shadow-rowa-panel rounded-rowa-lg backdrop-blur-rowa-overlay w-56 p-2"
          >
            <DropdownMenuLabel className="text-rowa-label-sm text-rowa-text-tertiary tracking-[0.08em] uppercase">
              Compare against
            </DropdownMenuLabel>
            {BENCHMARK_OPTIONS.map((option) => (
              <DropdownMenuCheckboxItem
                key={option.symbol}
                checked={benchmarks.includes(option.symbol)}
                onCheckedChange={(checked) =>
                  toggleBenchmark(option.symbol, checked === true)
                }
                // Keep the menu open so several can be checked in one visit.
                onSelect={(event) => event.preventDefault()}
                className="rounded-rowa-sm"
              >
                {option.name}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      }
      legend={
        growth.length > 0 ? (
          <>
            {series.map((item) => (
              <ChartPanelLegendItem
                key={item.key}
                color={item.color}
                label={item.name}
              />
            ))}
          </>
        ) : undefined
      }
      footnote={
        <span className="flex flex-wrap items-center justify-between gap-2">
          <span>
            Your line starts at $100 on the first day shown. Money you add or
            take out is removed, so what is left is performance.
          </span>
          <MethodologySheet />
        </span>
      }
    >
      <div className="grid gap-6 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="min-w-0">
          {isLoading ? (
            <Skeleton className="h-72 w-full" />
          ) : isError ? (
            <div className="flex min-h-64 items-center justify-center text-center">
              <div>
                <TriangleAlert
                  className="text-rowa-status-warning mx-auto size-5"
                  aria-hidden
                />
                <p className="text-rowa-body-md text-rowa-text-primary mt-3">
                  Performance is unavailable.
                </p>
                <p className="text-rowa-body-sm text-rowa-text-secondary mt-1">
                  Retry after the portfolio service reconnects.
                </p>
              </div>
            </div>
          ) : growth.length === 0 ? (
            <div className="flex min-h-64 items-center justify-center text-center">
              {isFunded ? (
                <div>
                  <p className="text-rowa-body-md text-rowa-text-primary">
                    Your return cannot be charted for this period yet.
                  </p>
                  <p className="text-rowa-body-sm text-rowa-text-secondary mt-1">
                    The daily return could not be worked out for these dates.
                    Choose another period, or check back after the next daily
                    update.
                  </p>
                </div>
              ) : (
                <div>
                  <p className="text-rowa-body-md text-rowa-text-primary">
                    Performance history begins after your first deposit.
                  </p>
                  <p className="text-rowa-body-sm text-rowa-text-secondary mt-1">
                    The chart appears once the first daily value is recorded,
                    usually the day after funding.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="min-w-0">
              <GrowthChart
                data={chartData}
                series={series}
                markers={markers}
                yDomain={yDomain}
              />
            </div>
          )}

          {warnings.length > 0 ? (
            <div className="bg-rowa-surface-accent text-rowa-body-sm text-rowa-text-secondary rounded-rowa-md mt-4 flex items-start gap-2 p-3">
              <TriangleAlert
                className="text-rowa-status-warning mt-0.5 size-4 shrink-0"
                aria-hidden
              />
              <div className="space-y-1">
                {warnings.map((warning, index) => (
                  // Two served warnings can read identically (one per vault
                  // under the same code), so the position is the key.
                  <p key={index}>
                    {warning.message}
                    {warning.detail ? (
                      <span className="text-rowa-text-tertiary">
                        {' '}
                        {warning.detail}
                      </span>
                    ) : null}
                  </p>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        {/* Same card, own rows: the table still states the numbers when
            the chart itself is loading or failed. */}
        <PeriodReturns
          rows={periodRows}
          benchmark={periodBenchmark}
          benchmarkLoading={periodBenchmarkLoading}
          benchmarkError={periodBenchmarkError}
        />
      </div>
    </ChartPanel>
  );
}
